import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const RecommendationTracker = ({ recommendations, investigations }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const filteredRecommendations = useMemo(() => {
    return recommendations?.filter(rec => {
      if (statusFilter !== 'all' && rec?.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && rec?.priority !== priorityFilter) return false;
      return true;
    });
  }, [recommendations, statusFilter, priorityFilter]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      'in-progress': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    return colors?.[status] || colors?.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30',
      high: 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30',
      medium: 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30',
      low: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30'
    };
    return colors?.[priority] || colors?.medium;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'engineering-control': 'Engineering Control',
      'administrative-control': 'Administrative Control',
      'training': 'Training',
      'maintenance': 'Maintenance',
      'ppe': 'Personal Protective Equipment'
    };
    return labels?.[category] || category;
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'overdue', label: 'Overdue' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <Select
          label="Status"
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
        />
        <Select
          label="Priority"
          options={priorityOptions}
          value={priorityFilter}
          onChange={setPriorityFilter}
        />
      </div>

      <div className="space-y-4">
        {filteredRecommendations?.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Icon name="CheckCircle" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No recommendations found matching your filters</p>
          </div>
        ) : (
          filteredRecommendations?.map(rec => (
            <div key={rec?.id} className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-sm font-semibold text-primary">
                      {rec?.incidentId}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(rec?.status)}`}>
                      {rec?.status?.toUpperCase()?.replace('-', ' ')}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(rec?.priority)}`}>
                      {rec?.priority?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-base font-medium text-foreground mb-3">
                    {rec?.recommendation}
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Tag" size={14} />
                      <span>{getCategoryLabel(rec?.category)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="User" size={14} />
                      <span>{rec?.assignedTo}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Briefcase" size={14} />
                      <span>{rec?.responsibleParty}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Implementation Progress</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-background rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          rec?.status === 'completed' ? 'bg-emerald-600' : 'bg-primary'
                        }`}
                        style={{ width: `${rec?.implementationProgress}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-foreground">{rec?.implementationProgress}%</span>
                  </div>
                </div>

                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Estimated Cost</p>
                  <p className="text-sm font-semibold text-foreground">
                    ${rec?.estimatedCost?.toLocaleString()}
                  </p>
                  {rec?.actualCost && (
                    <p className="text-xs text-muted-foreground">
                      Actual: ${rec?.actualCost?.toLocaleString()}
                    </p>
                  )}
                </div>

                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Deadline</p>
                  <p className="text-sm font-semibold text-foreground">
                    {format(new Date(rec?.implementationDeadline), 'MMM dd, yyyy')}
                  </p>
                  {rec?.completionDate && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">
                      Completed: {format(new Date(rec?.completionDate), 'MMM dd, yyyy')}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center gap-2">
                  {rec?.status === 'overdue' && (
                    <div className="flex items-center gap-1 text-red-600 dark:text-red-400 text-sm">
                      <Icon name="AlertTriangle" size={14} />
                      <span className="font-medium">Action Overdue</span>
                    </div>
                  )}
                  {rec?.status === 'completed' && (
                    <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-sm">
                      <Icon name="CheckCircle" size={14} />
                      <span className="font-medium">Implemented Successfully</span>
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="sm" iconName="Eye">
                  View Details
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecommendationTracker;